import styled from "@emotion/styled";
import { FC, useState } from "react";
import { useSelector } from "react-redux";

import { Apps, Automation, Chart, CreditCardOutline, OpenInNew, People, Settings } from "@livechat/design-system-icons";
import {
  ActionMenu,
  Badge,
  Button,
  DesignToken,
  Icon,
  Info,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { IconsEnum, Icon as LegacyIcon } from "components/icon";
import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { openInNewCard } from "helpers/routing";
import { NavigationItems } from "interfaces/navigationItem";
import { SubscriptionRoutes } from "routes/subscription";
import { selectIsPastDue } from "store/entities/license";
import { withVar } from "themes/helpers";

export const LiveChatMenu: FC = () => {
  const isPastDue = useSelector(selectIsPastDue);
  const [isVisible, setIsVisible] = useState(false);

  const handleOptionClick = (item: string): void => {
    trackEngagement("Tickets: LiveChat menu item clicked", EventPlace.Tickets, { item });
    openInNewCard(`/${item}`);
    setIsVisible(false);
  };

  const handleUpdateBillingDetails = (): void => {
    trackEngagement("Tickets: Update billing details clicked", EventPlace.Tickets);
    openInNewCard(SubscriptionRoutes.AccountDetails);
  };

  const toggleMenu = (): void => {
    setIsVisible(visible => !visible);
  };

  return (
    <Container>
      <ButtonWrapper>
        <ActionMenu
          visible={isVisible}
          placement="top"
          triggerRenderer={
            <Button kind="secondary" data-testid="livechat-menu-trigger" fullWidth onClick={toggleMenu}>
              Manage your HelpDesk
            </Button>
          }
          options={[
            {
              key: NavigationItems.Agents,
              element: (
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Icon source={People} />
                  <Text>Agents</Text>
                </Flex>
              ),
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              onClick: () => handleOptionClick(NavigationItems.Agents),
            },
            {
              key: NavigationItems.Reports,
              element: (
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Icon source={Chart} />
                  <Text>Reports</Text>
                </Flex>
              ),
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              onClick: () => handleOptionClick(NavigationItems.Reports),
            },
            {
              key: NavigationItems.Settings,
              element: (
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Icon source={Settings} />
                  <Text>Settings</Text>
                </Flex>
              ),
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              onClick: () => handleOptionClick(NavigationItems.Settings),
            },
            {
              key: NavigationItems.Automations,
              element: (
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Icon source={Automation} />
                  <Text>Automations</Text>
                </Flex>
              ),
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              onClick: () => handleOptionClick(NavigationItems.Automations),
            },
            {
              key: NavigationItems.Marketplace,
              element: (
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Icon source={Apps} />
                  <Text>Marketplace</Text>
                </Flex>
              ),
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              onClick: () => handleOptionClick(NavigationItems.Marketplace),
              withDivider: true,
            },
            {
              key: NavigationItems.Subscription,
              element: (
                <Flex gap={withVar(SpacingToken.Spacing2)}>
                  <Icon source={CreditCardOutline} />
                  <Text>Subscription</Text>
                </Flex>
              ),
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              onClick: () => handleOptionClick(NavigationItems.Subscription),
            },
          ]}
        />
      </ButtonWrapper>
      {isPastDue && (
        <Tooltip
          triggerRenderer={<ClickableBadge type="alert" size="compact" data-testid="past-due-badge" />}
          triggerOnClick
        >
          <TooltipLayout>
            <LegacyIcon name={IconsEnum.WarningDrawn} width="48px" height="48px" />
            <Info
              header="Payment needed"
              text="There was an issue processing your subscription payment. To continue using HelpDesk without restrictions, please update your payment details."
              closeWithX
            />
            <Button
              kind="high-contrast"
              icon={<Icon source={OpenInNew} />}
              onClick={handleUpdateBillingDetails}
              size="compact"
              iconPosition="right"
              data-testid="update-billing-details-button"
            >
              Update billing details
            </Button>
          </TooltipLayout>
        </Tooltip>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  border-top: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  padding: ${withVar(SpacingToken.Spacing2)} ${withVar(SpacingToken.Spacing5)};
  gap: ${withVar(SpacingToken.Spacing2)};
`;

const ButtonWrapper = styled.div`
  flex-grow: 1;
`;

const ClickableBadge = styled(Badge)`
  cursor: pointer;
`;

const TooltipLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${withVar(SpacingToken.Spacing3)};
`;
